import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  attachCuratedModelDragImage,
  CURATED_MODEL_DRAG_MIME,
} from '../utils/photorealistic/placedCuratedModelsStorage';
import {
  applyThumbMapToCuratedItems,
  fetchRoomObjectThumbMap,
} from '../utils/photorealistic/roomObjectThumbnails';

const LAYER_REORDER_MIME = 'application/x-dx-layer-reorder';

function reorderDisplayLayers(displayLayers, fromIndex, toIndex) {
  if (
    fromIndex == null ||
    toIndex == null ||
    fromIndex === toIndex ||
    fromIndex < 0 ||
    toIndex < 0 ||
    fromIndex >= displayLayers.length ||
    toIndex >= displayLayers.length
  ) {
    return displayLayers;
  }
  const next = [...displayLayers];
  const [moved] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, moved);
  return next;
}

const actionButtonClass =
  'flex flex-row justify-center items-center gap-2 w-full rounded-full border border-gray-200 bg-white px-3 py-2 text-sm font-medium text-primary-300 hover:bg-primary-500 hover:border-primary-300 hover:text-primary-300 transition-colors disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-white disabled:hover:border-gray-200 disabled:hover:text-primary-300';

const thumbBoxClass =
  'relative h-24 w-24 shrink-0 overflow-hidden rounded bg-[#ffffff]';

const thumbCheckerStyle = {
  backgroundSize: '8px 8px',
  backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
};

function CuratedModelThumb({ item, loading, broken, onThumbError }) {
  const showImage = Boolean(item.thumbSrc) && !broken;
  return (
    <div className={thumbBoxClass} style={thumbCheckerStyle}>
      {loading && !showImage ? (
        <div className="absolute inset-0 flex items-center justify-center bg-white/70 text-[10px] text-gray-400">
          …
        </div>
      ) : null}
      {showImage ? (
        <img
          src={item.thumbSrc}
          alt={item.name || 'Model'}
          className="h-full w-full object-contain"
          draggable={false}
          referrerPolicy="no-referrer"
          onError={() => onThumbError?.(item.id)}
        />
      ) : !loading ? (
        <div className="absolute inset-0 bg-gray-100/80" aria-hidden="true" />
      ) : null}
    </div>
  );
}

function CuratedModelCard({
  item,
  loading,
  broken,
  onThumbError,
  onRemove,
  dragEnabled,
  onDragStart,
  onDragEnd,
}) {
  return (
    <li
      draggable={dragEnabled}
      onDragStart={onDragStart}
      onDragEnd={onDragEnd}
      className={`relative flex w-[7rem] flex-col items-center gap-1.5 rounded-lg border border-gray-200 bg-white/80 px-2 py-2 ${
        dragEnabled ? 'cursor-grab active:cursor-grabbing' : ''
      }`}
      title={dragEnabled ? 'Drag onto the canvas to place' : undefined}
    >
      {onRemove ? (
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onRemove(item.id);
          }}
          onMouseDown={(e) => e.stopPropagation()}
          className="absolute right-0.5 top-0.5 z-10 flex h-5 w-5 items-center justify-center rounded-full border border-gray-200 bg-white/95 text-gray-500 shadow-sm transition-colors hover:border-red-200 hover:bg-red-50 hover:text-red-600"
          aria-label={`Remove ${item.name || 'model'} from curated list`}
          title="Remove from curated list"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            width="10"
            height="10"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <line x1="18" y1="6" x2="6" y2="18" />
            <line x1="6" y1="6" x2="18" y2="18" />
          </svg>
        </button>
      ) : null}
      <CuratedModelThumb
        item={item}
        loading={loading}
        broken={broken}
        onThumbError={onThumbError}
      />
      <div className="w-full min-w-0 text-center">
        <p className="truncate text-[10px] font-medium text-gray-800">{item.name || 'Model'}</p>
        <p className="truncate text-[9px] text-gray-500">{item.category || 'Other'}</p>
      </div>
    </li>
  );
}

function useCuratedModelsWithThumbs(models, roomId, onModelsChange) {
  const [resolvedById, setResolvedById] = useState({});
  const [loading, setLoading] = useState(false);
  const [brokenThumbIds, setBrokenThumbIds] = useState(() => new Set());
  const fetchGenRef = useRef(0);
  const lastAttemptKeyRef = useRef('');

  const modelIdsKey = useMemo(
    () =>
      models
        .map((m) => m?.id)
        .filter((id) => id != null)
        .map(String)
        .sort()
        .join(','),
    [models],
  );

  useEffect(() => {
    lastAttemptKeyRef.current = '';
    setResolvedById({});
    setBrokenThumbIds(new Set());
  }, [modelIdsKey]);

  const displayModels = useMemo(
    () =>
      models.map((m) => ({
        ...m,
        thumbSrc: m.thumbSrc || resolvedById[String(m.id)] || '',
      })),
    [models, resolvedById],
  );

  const fetchThumbs = useCallback(
    async (force = false) => {
      if (roomId == null || !modelIdsKey) return;
      const ids = modelIdsKey.split(',').filter(Boolean);
      if (!force) {
        if (lastAttemptKeyRef.current === modelIdsKey) return;
        const allResolved = models.every((m) => m?.id == null || m.thumbSrc);
        if (allResolved) return;
        lastAttemptKeyRef.current = modelIdsKey;
      }

      const gen = fetchGenRef.current + 1;
      fetchGenRef.current = gen;
      setLoading(true);
      try {
        const thumbMap = await fetchRoomObjectThumbMap(roomId, ids);
        if (fetchGenRef.current !== gen) return;
        const merged = applyThumbMapToCuratedItems(models, thumbMap);
        const nextResolved = {};
        for (const m of merged) {
          if (m?.id != null && m.thumbSrc) nextResolved[String(m.id)] = m.thumbSrc;
        }
        setResolvedById((prev) => ({ ...prev, ...nextResolved }));
        setBrokenThumbIds(new Set());
        const thumbsChanged = merged.some(
          (m, i) => (m.thumbSrc || '') !== (models[i]?.thumbSrc || ''),
        );
        if (thumbsChanged) onModelsChange?.(merged);
      } catch (err) {
        console.warn('ModelStudioPanel: failed to load curated thumbnails', err);
      } finally {
        if (fetchGenRef.current === gen) setLoading(false);
      }
    },
    [roomId, modelIdsKey, models, onModelsChange],
  );

  useEffect(() => {
    fetchThumbs(false);
  }, [fetchThumbs]);

  const loadingIds = useMemo(() => {
    if (!loading) return new Set();
    return new Set(modelIdsKey.split(',').filter(Boolean));
  }, [loading, modelIdsKey]);

  const handleThumbError = useCallback(
    (id) => {
      if (id == null) return;
      setBrokenThumbIds((prev) => {
        const next = new Set(prev);
        next.add(String(id));
        return next;
      });
      fetchThumbs(true);
    },
    [fetchThumbs],
  );

  return {
    displayModels,
    loadingIds,
    brokenThumbIds,
    handleThumbError,
  };
}

function ModelListThumb({ item, showThumb = true, showCategory = true }) {
  return (
    <>
      {showThumb ? (
        <div
          className="relative h-24 w-24 shrink-0 overflow-hidden bg-[#f9fafb]"
          style={{
            backgroundColor: 'white',
            backgroundSize: '8px 8px',
            backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px',
          }}
        >
          {item.thumbSrc ? (
            <img
              src={item.thumbSrc}
              alt={item.name || 'Model'}
              className="h-full w-full object-contain"
              draggable={false}
              referrerPolicy="no-referrer"
            />
          ) : null}
        </div>
      ) : null}
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-medium text-gray-800">{item.name || 'Model'}</p>
        {showCategory ? (
          <p className="truncate text-[10px] text-gray-500">{item.category || 'Other'}</p>
        ) : null}
      </div>
    </>
  );
}

const ModelStudioPanel = ({
  roomId = null,
  curatedLibraryModels = [],
  onCuratedLibraryModelsChange = null,
  onRemoveCuratedModel = null,
  canvasPlacements = [],
  selectedPlacementId = null,
  onSelectPlacement,
  onReorderPlacements,
  onOpenLibrary,
  onOpenQuickAdd,
  onOpenTransformModelsPanel,
  curatedDragEnabled = true,
  curatedDragPreviewWidthPx = 72,
}) => {
  const dragGhostCleanupRef = useRef(null);
  const [layersViewActive, setLayersViewActive] = useState(false);
  const [layerDragIndex, setLayerDragIndex] = useState(null);
  const [layerDropIndex, setLayerDropIndex] = useState(null);

  const { displayModels, loadingIds, brokenThumbIds, handleThumbError } = useCuratedModelsWithThumbs(
    curatedLibraryModels,
    roomId,
    onCuratedLibraryModelsChange,
  );

  const layerItemsTopFirst = useMemo(
    () => [...canvasPlacements].reverse(),
    [canvasPlacements],
  );

  const showCuratedList = () => setLayersViewActive(false);

  const applyLayerReorder = useCallback(
    (fromIndex, toIndex) => {
      if (!onReorderPlacements || fromIndex === toIndex) return;
      const reorderedDisplay = reorderDisplayLayers(layerItemsTopFirst, fromIndex, toIndex);
      onReorderPlacements([...reorderedDisplay].reverse());
    },
    [layerItemsTopFirst, onReorderPlacements],
  );

  const clearLayerDrag = useCallback(() => {
    setLayerDragIndex(null);
    setLayerDropIndex(null);
  }, []);

  const handleOpenLibrary = () => {
    showCuratedList();
    onOpenLibrary?.();
  };

  const handleLayersToggle = () => {
    setLayersViewActive((v) => !v);
  };

  const handleCuratedDragEnd = useCallback(() => {
    dragGhostCleanupRef.current?.();
    dragGhostCleanupRef.current = null;
  }, []);

  const handleCuratedDragStart = (event, item) => {
    if (!curatedDragEnabled || item?.id == null) return;
    if (event.target?.closest?.('button')) {
      event.preventDefault();
      return;
    }
    const payload = JSON.stringify({
      id: item.id,
      name: item.name ?? 'Model',
      category: item.category ?? 'Other',
      thumbSrc: item.thumbSrc ?? '',
    });
    event.dataTransfer.setData(CURATED_MODEL_DRAG_MIME, payload);
    event.dataTransfer.setData('application/json', payload);
    event.dataTransfer.effectAllowed = 'copy';
    dragGhostCleanupRef.current?.();
    const thumbImg = event.currentTarget?.querySelector?.('img');
    dragGhostCleanupRef.current = attachCuratedModelDragImage(
      event.dataTransfer,
      thumbImg || item.thumbSrc || '',
      curatedDragPreviewWidthPx,
    );
  };
  return (
    <div className="h-full min-h-0 overflow-y-auto">
      <div className="mb-3">
        <h3 className="text-[16px] lao-mn text-gray-700">Model Studio</h3>
      </div>

      <div className="mb-3">
        <input
          type="text"
          placeholder="Search..."
          className="w-full rounded-full bg-white px-3 py-2 text-sm text-gray-800 placeholder:text-gray-400 focus:outline-none"
        />
      </div>

      <div className="mb-10 grid grid-cols-2 gap-2">
        <button type="button" className={actionButtonClass}>
          <svg
            className="w-4 h-4"
            viewBox="0 0 14 10"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.1547"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M3.65733 0.577148H12.8949M3.65733 4.81105H12.8949M3.65733 9.04495H12.8949M0.578125 0.577148H1.34793M0.578125 4.81105H1.34793M0.578125 9.04495H1.34793" />
          </svg>
          Category
        </button>
        <button type="button" onClick={handleOpenLibrary} className={actionButtonClass}>
          <svg
            className="w-4 h-4"
            viewBox="0 0 13 12"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M11.1667 2.5H6.27333C5.94878 2.49986 5.63542 2.38134 5.392 2.16667L3.87733 0.833333H9.5C9.94203 0.833333 10.366 1.00893 10.6785 1.32149C10.9911 1.63405 11.1667 2.05797 11.1667 2.5ZM11.1667 2.5C11.5203 2.5 11.8594 2.64048 12.1095 2.89052C12.3595 3.14057 12.5 3.47971 12.5 3.83333L12.5 9.83333C12.5 10.187 12.3595 10.5261 12.1095 10.7761C11.8594 11.0262 11.5203 11.1667 11.1667 11.1667H1.83333C1.47971 11.1667 1.14057 11.0262 0.890524 10.7761C0.640475 10.5261 0.5 10.187 0.5 9.83333L0.5 1.83333C0.5 1.47971 0.640476 1.14057 0.890524 0.890524C1.14057 0.640476 1.47971 0.5 1.83333 0.5H2.99667C3.32122 0.500142 3.63458 0.61866 3.878 0.833333" />
          </svg>
          Library
        </button>
        <button type="button" onClick={onOpenQuickAdd} className={actionButtonClass}>
          <svg
            className="w-4 h-4"
            viewBox="0 0 15 16"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.1547"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M9.81573 7.88963H7.12143M7.12143 7.88963H4.42713M7.12143 7.88963V5.19533M7.12143 7.88963V10.5839M13.6647 10.8149V4.96439C13.6644 4.70831 13.5971 4.45676 13.4697 4.23465C13.3422 4.01255 13.159 3.82758 12.938 3.69806L7.84812 0.772823C7.62739 0.644656 7.37668 0.577148 7.12143 0.577148C6.86618 0.577148 6.61547 0.644656 6.39474 0.772823L1.30482 3.69806C1.08338 3.82699 0.899701 4.01183 0.772166 4.23407C0.644631 4.45631 0.577718 4.70815 0.578127 4.96439V10.8164C0.578624 11.0724 0.645927 11.3237 0.773379 11.5457C0.900831 11.7677 1.08402 11.9525 1.30482 12.082L6.39474 15.0072C6.61547 15.1354 6.86618 15.2029 7.12143 15.2029C7.37668 15.2029 7.62739 15.1354 7.84812 15.0072L12.938 12.082C13.1596 11.953 13.3433 11.768 13.4709 11.5456C13.5984 11.3232 13.6653 11.0712 13.6647 10.8149Z" />
          </svg>
          Quick Add
        </button>
        <button
          type="button"
          aria-pressed={layersViewActive}
          onClick={handleLayersToggle}
          className={`${actionButtonClass} ${
            layersViewActive ? 'border-primary-300 bg-primary-500/30' : ''
          }`}
        >
          <svg
            className="w-4 h-4"
            viewBox="0 0 13 12"
            fill="none"
            stroke="currentColor"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M12.5 5.83333L6.5 8.5L0.5 5.83333M12.5 8.5L6.5 11.1667L0.5 8.5M12.5 3.16667L6.5 5.83333L0.5 3.16667L6.5 0.5L12.5 3.16667Z" />
          </svg>
          Layers
        </button>
      </div>

      <div className="space-y-2">
        {layersViewActive ? (
          <>
            <div className="mb-3">
              <h3 className="text-[16px] lao-mn text-gray-700">Layers</h3>
            </div>
            {canvasPlacements.length === 0 ? (
              <p className="text-xs text-gray-500 px-0.5">
                No models on the canvas yet. Drag from Curated Models onto the canvas to add a layer.
              </p>
            ) : (
              <>
                <p className="text-xs text-gray-500 px-0.5 mb-1">
                  Drag to reorder. Top layer appears in front on the canvas.
                </p>
                <ul className="flex flex-col gap-1.5 pr-0.5">
                  {layerItemsTopFirst.map((item, index) => {
                    const isSelected = selectedPlacementId === item.placementId;
                    const isDragging = layerDragIndex === index;
                    const isDropTarget =
                      layerDropIndex === index &&
                      layerDragIndex != null &&
                      layerDragIndex !== index;
                    return (
                      <li
                        key={item.placementId}
                        data-layer-row
                        className={`list-none transition-colors ${
                          isDropTarget ? 'ring-2 ring-primary-200 rounded-lg' : ''
                        }`}
                        onDragOver={(e) => {
                          e.preventDefault();
                          e.dataTransfer.dropEffect = 'move';
                          setLayerDropIndex(index);
                        }}
                        onDragLeave={() => {
                          setLayerDropIndex((prev) => (prev === index ? null : prev));
                        }}
                        onDrop={(e) => {
                          e.preventDefault();
                          const raw = e.dataTransfer.getData(LAYER_REORDER_MIME);
                          const fromIndex = raw !== '' ? Number(raw) : layerDragIndex;
                          if (Number.isFinite(fromIndex)) {
                            applyLayerReorder(fromIndex, index);
                          }
                          clearLayerDrag();
                        }}
                      >
                        <div
                          className={`flex w-full min-w-0 items-stretch overflow-hidden rounded-lg border bg-white/80 ${
                            isSelected
                              ? 'border-primary-300 ring-2 ring-primary-200'
                              : 'border-gray-200'
                          } ${
                            isDragging
                              ? 'border-dashed border-gray-300 bg-gray-50/80 opacity-60'
                              : ''
                          }`}
                        >
                          <button
                            type="button"
                            onClick={() => onSelectPlacement?.(item.placementId)}
                            className="flex min-w-0 flex-1 px-2.5 py-2 text-left transition-colors hover:bg-white/90"
                            title="Select on canvas"
                          >
                            <ModelListThumb item={item} showThumb={false} showCategory={false} />
                          </button>
                          <button
                            type="button"
                            draggable
                            aria-label={`Reorder layer ${item.name || 'Model'}`}
                            title="Drag to reorder"
                            className="flex shrink-0 cursor-grab items-center justify-center border-l border-gray-200 bg-white/90 px-2 text-gray-400 active:cursor-grabbing hover:bg-gray-50 hover:text-gray-600"
                            onDragStart={(e) => {
                              e.stopPropagation();
                              e.dataTransfer.effectAllowed = 'move';
                              e.dataTransfer.setData(LAYER_REORDER_MIME, String(index));
                              const row = e.currentTarget.closest('[data-layer-row]');
                              if (row instanceof HTMLElement) {
                                e.dataTransfer.setDragImage(
                                  row,
                                  row.offsetWidth / 2,
                                  row.offsetHeight / 2,
                                );
                              }
                              setLayerDragIndex(index);
                              setLayerDropIndex(index);
                            }}
                            onDragEnd={clearLayerDrag}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <svg
                              xmlns="http://www.w3.org/2000/svg"
                              width="10"
                              height="14"
                              viewBox="0 0 10 14"
                              fill="currentColor"
                              aria-hidden
                            >
                              <circle cx="2.5" cy="2.5" r="1.25" />
                              <circle cx="7.5" cy="2.5" r="1.25" />
                              <circle cx="2.5" cy="7" r="1.25" />
                              <circle cx="7.5" cy="7" r="1.25" />
                              <circle cx="2.5" cy="11.5" r="1.25" />
                              <circle cx="7.5" cy="11.5" r="1.25" />
                            </svg>
                          </button>
                        </div>
                      </li>
                    );
                  })}
                </ul>
              </>
            )}
          </>
        ) : (
          <>
            <div className="mb-3">
              <h3 className="text-[16px] lao-mn text-gray-700">Curated Models</h3>
            </div>
            {curatedLibraryModels.length === 0 ? (
              <p className="text-xs text-gray-500 px-0.5">
                Open Library, select models, then Confirm to list them here.
              </p>
            ) : curatedDragEnabled ? (
              <p className="text-xs text-gray-500 px-0.5 mb-1">
                Drag a model onto the canvas to place it.
              </p>
            ) : null}
            {curatedLibraryModels.length > 0 ? (
              <ul className="flex flex-row flex-wrap gap-1.5 pr-0.5">
                {displayModels.map((item) => (
                  <CuratedModelCard
                    key={String(item.id)}
                    item={item}
                    loading={loadingIds.has(String(item.id)) && !item.thumbSrc}
                    broken={brokenThumbIds.has(String(item.id))}
                    onThumbError={handleThumbError}
                    onRemove={onRemoveCuratedModel}
                    dragEnabled={curatedDragEnabled}
                    onDragStart={(e) => handleCuratedDragStart(e, item)}
                    onDragEnd={handleCuratedDragEnd}
                  />
                ))}
              </ul>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
};

export default ModelStudioPanel;
